// Largeur des colonnes réglable : poser <ResizableColumns id="…" /> comme premier
// enfant d'un <table>. Tirer le bord droit d'un en-tête change la largeur,
// double-clic sur ce bord rend la largeur d'origine. Mémorisé par utilisateur.
import { useEffect, useRef } from 'react';
import {
  clampColumnWidth,
  columnKey,
  columnWidthsStorageKey,
  isOnColumnGrip,
  type ColumnWidths,
} from '../../lib/columnWidths';
import { useAuthStore } from '../../store/useAuthStore';
import { useColumnWidthsStore } from '../../store/useColumnWidthsStore';

function headerCells(table: HTMLTableElement): HTMLTableCellElement[] {
  const row = table.tHead?.rows[0];
  return row ? Array.from(row.cells) : [];
}

function keyOf(th: HTMLTableCellElement, index: number): string {
  return columnKey(th.dataset.col, th.textContent, index);
}

function applyWidth(cell: HTMLTableCellElement, px: number | undefined, clip = false) {
  const value = px == null ? '' : `${px}px`;
  if (cell.style.width === value) return;
  cell.style.width = value;
  cell.style.minWidth = value;
  cell.style.maxWidth = value;
  // Cellule du corps : le contenu trop long est coupé plutôt que de repousser la colonne.
  if (clip) {
    cell.style.overflow = px == null ? '' : 'hidden';
    cell.style.textOverflow = px == null ? '' : 'ellipsis';
  }
}

/** Largeur d'une colonne : l'en-tête, puis chaque ligne du corps qui a le même nombre de cellules. */
function applyColumn(table: HTMLTableElement, index: number, count: number, px: number | undefined) {
  const head = headerCells(table)[index];
  if (head) applyWidth(head, px);
  for (const body of Array.from(table.tBodies)) {
    for (const row of Array.from(body.rows)) {
      if (row.cells.length !== count) continue; // ligne de groupe ou d'état vide (colspan)
      const cell = row.cells[index];
      if (cell) applyWidth(cell, px, true);
    }
  }
}

function applyAll(table: HTMLTableElement, widths: ColumnWidths) {
  const heads = headerCells(table);
  heads.forEach((th, i) => applyColumn(table, i, heads.length, widths[keyOf(th, i)]));
}

export function ResizableColumns({ id }: { id: string }) {
  const anchor = useRef<HTMLTableColElement>(null);
  const collaboratorId = useAuthStore((s) => s.collaborator?.id) ?? 'anon';
  const storageKey = columnWidthsStorageKey(collaboratorId, id);

  useEffect(() => {
    const table = anchor.current?.closest('table');
    if (!table) return;
    const read = () => useColumnWidthsStore.getState().widths[storageKey] ?? {};
    const save = (column: string, px: number | null) =>
      useColumnWidthsStore.getState().setWidth(collaboratorId, id, column, px);

    applyAll(table, read());
    // Les en-têtes changent quand on affiche ou masque une colonne : on réapplique.
    const observer = new MutationObserver(() => applyAll(table, read()));
    // Idem pour les lignes : React en ajoute et en retire, sans nos largeurs.
    observer.observe(table, { childList: true, subtree: true });
    const unsubscribe = useColumnWidthsStore.subscribe(() => applyAll(table, read()));

    function gripTarget(e: MouseEvent): HTMLTableCellElement | null {
      const th = (e.target as HTMLElement | null)?.closest('th');
      if (!th || th.parentElement?.parentElement !== table!.tHead) return null;
      const rect = th.getBoundingClientRect();
      return isOnColumnGrip(e.clientX - rect.left, rect.width) ? th : null;
    }

    let drag: { th: HTMLTableCellElement; key: string; startX: number; startW: number } | null = null;
    let dragged = false;

    function onMove(e: MouseEvent) {
      if (drag) return;
      const head = table!.tHead;
      if (head) head.style.cursor = gripTarget(e) ? 'col-resize' : '';
    }
    function onDragMove(e: MouseEvent) {
      if (!drag) return;
      dragged = true;
      applyColumn(
        table!,
        drag.th.cellIndex,
        headerCells(table!).length,
        clampColumnWidth(drag.startW + e.clientX - drag.startX),
      );
    }
    function onDragEnd(e: MouseEvent) {
      if (!drag) return;
      const { key, startW, startX } = drag;
      drag = null;
      document.body.style.userSelect = '';
      document.removeEventListener('mousemove', onDragMove);
      document.removeEventListener('mouseup', onDragEnd);
      if (dragged) save(key, clampColumnWidth(startW + e.clientX - startX));
    }
    function onDown(e: MouseEvent) {
      if (e.button !== 0) return;
      const th = gripTarget(e);
      if (!th) return;
      // La poignée ne doit ni trier la colonne ni sélectionner du texte.
      e.preventDefault();
      e.stopPropagation();
      dragged = false;
      drag = {
        th,
        key: keyOf(th, th.cellIndex),
        startX: e.clientX,
        startW: th.getBoundingClientRect().width,
      };
      document.body.style.userSelect = 'none';
      document.addEventListener('mousemove', onDragMove);
      document.addEventListener('mouseup', onDragEnd);
    }
    function onClick(e: MouseEvent) {
      if (gripTarget(e)) {
        e.preventDefault();
        e.stopPropagation();
      }
    }
    function onDoubleClick(e: MouseEvent) {
      const th = gripTarget(e);
      if (!th) return;
      e.preventDefault();
      e.stopPropagation();
      save(keyOf(th, th.cellIndex), null);
    }

    const head = table.tHead;
    head?.addEventListener('mousemove', onMove);
    head?.addEventListener('mousedown', onDown, true);
    head?.addEventListener('click', onClick, true);
    head?.addEventListener('dblclick', onDoubleClick, true);
    return () => {
      observer.disconnect();
      unsubscribe();
      head?.removeEventListener('mousemove', onMove);
      head?.removeEventListener('mousedown', onDown, true);
      head?.removeEventListener('click', onClick, true);
      head?.removeEventListener('dblclick', onDoubleClick, true);
      document.removeEventListener('mousemove', onDragMove);
      document.removeEventListener('mouseup', onDragEnd);
      document.body.style.userSelect = '';
    };
  }, [collaboratorId, id, storageKey]);

  // <colgroup> vide : seul enfant « neutre » admis en tête de <table>, il sert d'ancre.
  return <colgroup ref={anchor} />;
}
